import React from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
    IconBell,
    IconCalendar,
    IconCheck,
    IconInfoCircle,
    IconShoppingBag,
    IconTicket,
    IconUser,
    IconUserPlus
} from '@tabler/icons';

const TYPE_CONFIG = {
    product: {
        label: 'Produto',
        color: '#16a34a',
        gradient: 'linear-gradient(135deg,#16a34a,#22c55e)',
        icon: IconShoppingBag
    },
    lead: {
        label: 'Lead',
        color: '#2563eb',
        gradient: 'linear-gradient(135deg,#2563eb,#60a5fa)',
        icon: IconUserPlus
    },
    calendar: {
        label: 'Calendário',
        color: '#ea580c',
        gradient: 'linear-gradient(135deg,#ea580c,#fb923c)',
        icon: IconCalendar
    },
    user: {
        label: 'Usuário',
        color: '#7c3aed',
        gradient: 'linear-gradient(135deg,#7c3aed,#a855f7)',
        icon: IconUser
    },
    ticket: {
        label: 'Ticket',
        color: '#0891b2',
        gradient: 'linear-gradient(135deg,#0891b2,#22d3ee)',
        icon: IconTicket
    },
    system: {
        label: 'Sistema',
        color: '#64748b',
        gradient: 'linear-gradient(135deg,#64748b,#94a3b8)',
        icon: IconInfoCircle
    }
};

const formatRelative = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return `${Math.floor(diff / 86400)}d atrás`;
};

const NotificationList = ({ notifications = [], onMarkRead, loading = false }) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (notifications.length === 0) {
        return (
            <Box sx={{ py: 5, px: 2, textAlign: 'center', color: 'text.secondary' }}>
                <IconBell size={36} opacity={0.25} />
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.5 }}>
                    Nenhuma notificação
                </Typography>
            </Box>
        );
    }

    return (
        <List disablePadding sx={{ width: '100%', maxWidth: 360 }}>
            {notifications.map((notification, index) => {
                const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.system;
                const Icon = config.icon;

                return (
                    <React.Fragment key={notification.id}>
                        <ListItem
                            alignItems="flex-start"
                            sx={{
                                px: 2,
                                py: 1.5,
                                background: notification.read ? 'transparent' : `${config.color}08`,
                                borderLeft: notification.read ? '3px solid transparent' : `3px solid ${config.color}`,
                                transition: 'background 0.2s',
                                '&:hover': {
                                    background: `${config.color}10`
                                }
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 38,
                                    height: 38,
                                    mr: 1.5,
                                    mt: 0.25,
                                    flexShrink: 0,
                                    background: notification.read
                                        ? (theme) => theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9'
                                        : config.gradient
                                }}
                            >
                                <Icon size={18} color={notification.read ? config.color : '#fff'} />
                            </Avatar>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                    <Chip
                                        label={config.label}
                                        size="small"
                                        sx={{
                                            height: 16,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            background: config.gradient,
                                            color: '#fff',
                                            '& .MuiChip-label': { px: 0.75 }
                                        }}
                                    />
                                </Box>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: notification.read ? 400 : 600,
                                        color: 'text.primary',
                                        lineHeight: 1.4,
                                        wordBreak: 'break-word',
                                        fontSize: 13
                                    }}
                                >
                                    {notification.message}
                                </Typography>

                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                                    {formatRelative(notification.created_at)}
                                </Typography>
                            </Box>

                            {!notification.read && onMarkRead && (
                                <Tooltip title="Marcar como lida">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onMarkRead(notification.id);
                                        }}
                                        sx={{
                                            ml: 0.5,
                                            mt: 0.5,
                                            flexShrink: 0,
                                            width: 28,
                                            height: 28,
                                            color: config.color,
                                            background: `${config.color}15`,
                                            borderRadius: '8px',
                                            '&:hover': { background: `${config.color}28` }
                                        }}
                                    >
                                        <IconCheck size={14} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </ListItem>

                        {index < notifications.length - 1 && <Divider sx={{ mx: 2, opacity: 0.5 }} />}
                    </React.Fragment>
                );
            })}
        </List>
    );
};

export default NotificationList;
